class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.discountApplyButtons = document.querySelectorAll('.cart-discount-apply-button');
    this.removeDiscountCodeButtons = document.querySelectorAll('.cart__discount-code-clear-codes__button');
    this.cartErrors = document.getElementById('cart-errors');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, input) => total + parseInt(input.value, 10), 0);

    this.requestId = 0;

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.discountApplyButtons.forEach((button) => {
      button.addEventListener('click', this.fetchDiscountCode.bind(this));
    });

    this.removeDiscountCodeButtons.forEach((button) => {
      button.addEventListener('click', this.removeDiscountCode.bind(this));
    });

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    this.addEventListener('focusin', (event) => {
      if (event.target?.name === 'updates[]') {
        event.target.setAttribute('data-previous-value', event.target.value);
      }
    });
  }

  onChange(event) {
    if (!event.target) return;

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement?.getAttribute('name')
    );
  }

  getSectionsToRender() {
    if (this.sectionsToRender) return this.sectionsToRender;

    this.sectionsToRender = [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      },
    ];

    if (document.querySelector('#main-cart-footer .free-shipping')) {
      this.sectionsToRender.push({
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.free-shipping',
      });
    }

    return this.sectionsToRender;
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const currentRequestId = ++this.requestId;
    const sections = this.getSectionsToRender().map((section) => section.section);

    const body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: window.location.pathname,
    });

    fetch(`${window.routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        if (currentRequestId !== this.requestId) return;

        const parsedState = JSON.parse(state);

        this.classList.toggle('is-empty', parsedState.item_count === 0);

        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) {
          cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        }

        this.renderSections(parsedState);
        this.updateLiveRegions(line, parsedState.item_count);

        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && name) {
          lineItem.querySelector(`[name="${name}"]`)?.focus();
        }

        this.disableLoading();

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart: parsedState },
          })
        );
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.add('hidden')
        );
        this.disableLoading();
        if (this.cartErrors) {
          this.cartErrors.textContent = window.cartStrings.error;
        }
      });
  }

  updateLiveRegions(line, itemCount) {
    const quantityInput = document.getElementById(`Quantity-${line}`);
    const quantityError = document.getElementById(`Line-item-error-${line}`);

    if (quantityError) {
      quantityError.querySelector('.cart-item__error-text').textContent = '';
    }

    if (quantityInput && quantityError) {
      const previousQuantity = parseInt(
        quantityInput.getAttribute('data-previous-value'),
        10
      );
      const currentQuantity = parseInt(quantityInput.value, 10);

      if (!Number.isNaN(previousQuantity) && previousQuantity === currentQuantity) {
        quantityError.querySelector('.cart-item__error-text').innerHTML =
          window.cartStrings.quantityError.replace('[quantity]', currentQuantity);
      }
    }

    this.currentItemCount = itemCount;

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus = document.getElementById('cart-live-region-text');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
    }
  }

  renderSections(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (!element) return;

      const elementToReplace = element.querySelector(section.selector) || element;
      if (parsedState.sections?.[section.section]) {
        elementToReplace.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }
    });
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML;
  }

  fetchDiscountCode(event) {
    const discountCode = event.target.parentNode.querySelector('input').value;

    fetch(`/discount/${discountCode}`)
      .then(() => {
        const sections = this.getSectionsToRender().map((section) => section.section);
        return fetch(`${window.routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            sections,
            sections_url: window.location.pathname,
          }),
        });
      })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.renderSections(parsedState);

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart: parsedState },
          })
        );
      })
      .catch((error) => {
        console.error('Failed to apply discount:', error);
      });
  }

  removeDiscountCode() {
    const sections = this.getSectionsToRender().map((section) => section.section);

    fetch(`${window.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        discount: '',
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.renderSections(parsedState);

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart: parsedState },
          })
        );
      });
  }

  enableLoading(line) {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.add('cart__items--disabled');

    const loadingOverlay = this.querySelectorAll('.loading-overlay')[line - 1];
    if (loadingOverlay) loadingOverlay.classList.remove('hidden');

    document.activeElement?.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading() {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.remove('cart__items--disabled');
  }
}

customElements.define('cart-items', CartItems);



class MiniCartUpsell extends CartItems {
  constructor() {
    super();
    this.initUpsellProductAdds();
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  initUpsellProductAdds() {
    this.addButtons = this.querySelectorAll(".mini-cart__upsell-add-button");

    this.addButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.classList.add("loading");

        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: button.dataset.variantId,
            quantity: 1,
          }),
        })
          .then((response) => response.json())
          .then(() => {
            const sections = this.getSectionsToRender().map(s => s.section).join('&sections[]=');
            return fetch(`${window.routes.cart_url}?sections[]=${sections}`);
          })
          .then((response) => response.json())
          .then((parsedState) => {
            this.getSectionsToRender().forEach((section) => {
              const element = document.getElementById(section.id);
              if (element && parsedState[section.section]) {
                const elementToReplace = element.querySelector(section.selector) || element;
                elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
              }
            });

            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
          })
          .finally(() => {
            button.classList.remove("loading");
          });
      });
    });
  }
}

customElements.define("mini-cart-upsell", MiniCartUpsell);
